import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useAuth } from '../lib/auth.jsx';
import { useData } from '../lib/data.jsx';
import { useUI, Sec, Row } from '../lib/ui.jsx';
import { supabase, rpc } from '../lib/supabase.js';
import { thDateTime, thTime } from '../lib/util.js';

// สิทธิ์ 17 รายการ (label + กลุ่ม + อ่อนไหว) — ตรงกับ seed ใน deploy-02
const PERM_GROUPS = [
  ['งานหน้าร้าน', [
    ['rent.create', 'เปิดสัญญาเช่า'],
    ['rent.return', 'รับคืนและตรวจสภาพ'],
    ['rent.book', 'รับจองล่วงหน้า'],
    ['cust.edit', 'เพิ่ม/แก้ไขข้อมูลลูกค้า'],
  ]],
  ['เรื่องเงินและส่วนลด', [
    ['deposit.waive', 'ลดหรือยกเว้นเงินประกัน', 1, 'กระทบความเสี่ยงของทรัพย์สินโดยตรง'],
    ['rate.special', 'อนุมัติเรทพิเศษ / ส่วนลดปริมาณ', 1],
    ['credit.use', 'อนุมัติใช้เครดิตเป็นส่วนลด'],
    ['damage.assess', 'ประเมินและเรียกเก็บค่าเสียหาย'],
    ['refund.deposit', 'อนุมัติคืนเงินประกัน', 1],
    ['tradein.accept', 'ประเมินและรับเทิร์นสินค้า', 1],
  ]],
  ['ข้อมูลอ่อนไหว', [
    ['id.view', 'เปิดดูเอกสารยืนยันตัวตน', 1, 'ทุกครั้งที่เปิดดูจะถูกบันทึกประวัติ'],
    ['id.delete', 'ลบเอกสารยืนยันตัวตน', 1],
    ['report.view', 'ดูรายงานรายรับ'],
    ['log.view', 'ดูประวัติการใช้งาน', 1, 'เห็นการกระทำของพนักงานทุกคน'],
  ]],
  ['ผู้ดูแลระบบ', [
    ['contract.edit', 'แก้ไขหรือยกเลิกสัญญาย้อนหลัง', 1, 'เปิดให้เฉพาะกรณีจำเป็น ตรวจสอบย้อนหลังได้ยาก'],
    ['staff.manage', 'เพิ่ม/ลบพนักงาน และกำหนดสิทธิ์', 1],
    ['system.edit', 'แก้ไขค่าระบบ ภาษี และอัตรา', 1],
  ]],
];

export default function Settings() {
  const { can } = useAuth();
  const [tab, setTab] = useState(can('staff.manage') ? 'perm' : 'sys');
  return (<>
    <div className="pagehead">
      <div className="eyebrow">ตั้งค่า · SETTINGS</div>
      <h1 className="bighead">พนักงานและสิทธิ์</h1>
    </div>
    <div className="catfilter">
      <button className={tab === 'perm' ? 'on' : ''} onClick={() => setTab('perm')}>สิทธิ์ตามระดับ</button>
      <button className={tab === 'staff' ? 'on' : ''} onClick={() => setTab('staff')}>พนักงาน</button>
      <button className={tab === 'sys' ? 'on' : ''} onClick={() => setTab('sys')}>ค่าระบบ</button>
    </div>
    {tab === 'perm' && <PermTab />}
    {tab === 'staff' && <StaffTab />}
    {tab === 'sys' && <SysTab />}
  </>);
}

// ---------- ตารางสิทธิ์ ----------
function PermTab() {
  const { can } = useAuth();
  const { roles, rolePerms, refresh } = useData();
  const { toast } = useUI();

  if (!can('staff.manage') && !can('log.view')) {
    return <div className="lockmsg"><b>ต้องใช้สิทธิ์ผู้ดูแลระบบ</b>ตารางสิทธิ์แก้ได้เฉพาะผู้มีสิทธิ์ "เพิ่ม/ลบพนักงาน และกำหนดสิทธิ์"</div>;
  }
  const allowed = (role, perm) => rolePerms.find(p => p.role_key === role && p.perm_key === perm)?.allowed || false;
  const count = (role) => rolePerms.filter(p => p.role_key === role && p.allowed).length;
  const editable = can('staff.manage');

  const setPerm = async (role, perm, val, label) => {
    try {
      await rpc('rp_set_permission', { p_role: role, p_perm: perm, p_allowed: val });
      toast(`${val ? 'เปิด' : 'ปิด'}สิทธิ์ "${label}" ให้${roles.find(r => r.key === role)?.name} · บันทึกประวัติแล้ว`);
      await refresh();
    } catch (e) { toast(e.message); }
  };

  return (
    <div className="card setpane">
      <h2>ตารางสิทธิ์</h2>
      <div className="hint">ติ๊กเพื่อเปิดสิทธิ์ให้ระดับนั้น · เจ้าของมีสิทธิ์ทุกอย่างเสมอ แก้ไม่ได้</div>
      <div className="permwrap">
        <table className="permtbl">
          <thead><tr>
            <th>สิทธิ์</th>
            {roles.map(r => <th key={r.key}>{r.name}<br />
              <span style={{ fontWeight: 400, opacity: .7, fontFamily: 'var(--mono)', fontSize: 9 }}>{count(r.key)} สิทธิ์</span></th>)}
          </tr></thead>
          <tbody>
            {PERM_GROUPS.map(([g, perms]) => (<GroupRows key={g} g={g} perms={perms} roles={roles}
              allowed={allowed} editable={editable} setPerm={setPerm} />))}
          </tbody>
        </table>
      </div>
      <div className="note show" style={{ marginTop: 14 }}>
        สิทธิ์ที่มีเครื่องหมาย <b>!</b> เป็นสิทธิ์อ่อนไหว ควรให้เฉพาะเท่าที่จำเป็น · การแก้ไขตารางนี้จะถูกบันทึกลงประวัติการใช้งานทุกครั้ง
      </div>
    </div>
  );
}

function GroupRows({ g, perms, roles, allowed, editable, setPerm }) {
  return (<>
    <tr className="grp"><td colSpan={roles.length + 1}>{g}</td></tr>
    {perms.map(([key, label, sens, note]) => (
      <tr key={key}>
        <td className="lbl">{sens ? <span className="sens">!</span> : null}{label}{note ? <small>{note}</small> : null}</td>
        {roles.map(r => (
          <td className="p" key={r.key}>
            <input type="checkbox" className="psw"
              checked={allowed(r.key, key)}
              disabled={r.key === 'owner' || !editable}
              onChange={e => setPerm(r.key, key, e.target.checked, label)} />
          </td>
        ))}
      </tr>
    ))}
  </>);
}

// ---------- พนักงาน ----------
function StaffTab() {
  const { can, me } = useAuth();
  const { staff, roles, branches, rolePerms, refresh } = useData();
  const { toast, openDrawer, closeDrawer } = useUI();
  const [showAdd, setShowAdd] = useState(false);
  const [nf, setNf] = useState({ name: '', email: '', role: 'staff' });
  const [busy, setBusy] = useState(false);

  if (!can('staff.manage')) {
    return <div className="lockmsg"><b>ต้องใช้สิทธิ์ผู้ดูแลระบบ</b>หน้าพนักงานเปิดได้เฉพาะผู้มีสิทธิ์ "เพิ่ม/ลบพนักงาน และกำหนดสิทธิ์"</div>;
  }
  const roleName = (k) => roles.find(r => r.key === k)?.name || k;

  const sendPwLink = async (s) => {
    try {
      if (!s.email) { toast('พนักงานคนนี้ยังไม่มีอีเมลในระบบ'); return; }
      const { error } = await supabase.auth.resetPasswordForEmail(s.email, { redirectTo: location.origin + '/?v=setpw' });
      if (error) throw new Error(error.message);
      await rpc('rp_staff_pw_link_sent', { p_id: s.id });
      toast(`ส่งลิงก์ตั้งรหัสผ่านให้ ${s.name} แล้ว — ให้เจ้าตัวเปิดจากอีเมลล่าสุด`);
    } catch (e) { toast(e.message); }
  };

  const flag = async (s, field, value, msg) => {
    try {
      await rpc('rp_staff_flag', { p_id: s.id, p_field: field, p_value: value });
      toast(msg); closeDrawer(); await refresh();
    } catch (e) { toast(e.message); }
  };

  const openStaff = (s) => {
    const mine = rolePerms.filter(p => p.role_key === s.role && p.allowed);
    openDrawer('พนักงาน · STAFF', s.name, (<>
      <Sec>ข้อมูล</Sec>
      <Row k="รหัสพนักงาน" v={s.emp_code || '—'} />
      <Row k="ระดับ" v={roleName(s.role)} />
      <Row k="อีเมล" v={s.email || '—'} />
      <Row k="สาขา" v={s.branch_name || '—'} />
      <Row k="เข้าระบบล่าสุด" v={s.last_seen ? thDateTime(s.last_seen) + ' ' + thTime(s.last_seen) : 'ยังไม่เคยเข้า'} />
      <Row k="สถานะ" v={s.is_active ? 'ใช้งาน' : 'ปิดใช้งาน'} gold={!s.is_active} />
      <Row k="ยืนยันตัวตนสองชั้น" v={s.twofa ? 'เปิด' : 'ปิด'} gold={!s.twofa && (s.role === 'mgr' || s.role === 'owner')} />
      <Sec>สิทธิ์ที่ได้รับ ({mine.length} รายการ — ตามระดับ{roleName(s.role)})</Sec>
      <div className="hint">แก้สิทธิ์ที่แท็บ "สิทธิ์ตามระดับ" — มีผลทั้งระดับ</div>
      <Sec>รหัสผ่าน</Sec>
      <div className="hint" style={{ marginBottom: 10 }}>
        ระบบไม่เก็บและไม่แสดงรหัสผ่านของพนักงาน ผู้ดูแลตั้งรหัสแทนพนักงานไม่ได้
        เพื่อให้ประวัติการใช้งานอ้างอิงตัวบุคคลได้จริง
      </div>
      <div className="dw-act">
        <button onClick={() => sendPwLink(s)}>ส่งลิงก์ตั้งรหัสผ่านใหม่ <span className="ar">→</span></button>
        <button onClick={() => flag(s, 'must_change_pw', true, 'จะบังคับเปลี่ยนรหัสครั้งถัดไปที่เข้าระบบ')}>บังคับเปลี่ยนรหัสครั้งถัดไป <span className="ar">→</span></button>
        <button onClick={() => flag(s, 'twofa', !s.twofa, s.twofa ? 'ปิด 2FA แล้ว' : 'เปิด 2FA แล้ว')}>{s.twofa ? 'ปิด' : 'เปิด'}ยืนยันตัวตนสองชั้น <span className="ar">→</span></button>
        {s.id !== me.id && (
          <button onClick={() => flag(s, 'is_active', !s.is_active, s.is_active ? 'ปิดใช้งานบัญชีแล้ว — เข้าระบบไม่ได้ทันที' : 'เปิดใช้งานบัญชีแล้ว')}>
            {s.is_active ? 'ปิดใช้งานบัญชีนี้ (ตัดการเข้าถึงทันที)' : 'เปิดใช้งานบัญชีนี้'} <span className="ar">→</span>
          </button>
        )}
      </div>
    </>));
  };

  const addStaff = async () => {
    if (!nf.name.trim() || !nf.email.trim()) { toast('กรอกชื่อและอีเมลก่อน'); return; }
    setBusy(true);
    try {
      // สร้างบัญชี auth ด้วย client แยก (ไม่แตะ session ของแอดมิน) — รหัสสุ่มทิ้ง
      // แล้วส่งลิงก์ให้พนักงานตั้งรหัสเอง: ไม่มีใครรู้รหัสของพนักงาน
      const tmp = createClient(process.env.RP_SUPABASE_URL, process.env.RP_SUPABASE_ANON_KEY,
        { auth: { persistSession: false, autoRefreshToken: false } });
      const rand = crypto.randomUUID() + crypto.randomUUID();
      const { data, error } = await tmp.auth.signUp({ email: nf.email.trim(), password: rand });
      let uid = data?.user?.id;
      if (error) {
        if (/already registered/i.test(error.message)) {
          toast('อีเมลนี้มีบัญชีอยู่แล้ว — ถ้าเป็นพนักงานคนเดิมให้ติดต่อผู้ดูแล'); setBusy(false); return;
        }
        throw new Error(error.message);
      }
      if (!uid) throw new Error('สร้างบัญชีไม่สำเร็จ');
      const code = 'E-' + String(staff.length + 1).padStart(2, '0');
      await rpc('rp_staff_upsert', {
        p: { id: uid, emp_code: code, name: nf.name.trim(), role: nf.role, email: nf.email.trim(), is_active: true },
      });
      const { error: e2 } = await supabase.auth.resetPasswordForEmail(nf.email.trim(), { redirectTo: location.origin + '/?v=setpw' });
      if (!e2) await rpc('rp_staff_pw_link_sent', { p_id: uid });
      toast(`เพิ่ม ${nf.name} แล้ว · ส่งลิงก์ตั้งรหัสผ่านไปที่อีเมลให้เจ้าตัวตั้งเอง`);
      setShowAdd(false); setNf({ name: '', email: '', role: 'staff' });
      await refresh();
    } catch (e) { toast(e.message); }
    setBusy(false);
  };

  return (
    <div className="card setpane">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div><h2>รายชื่อพนักงาน</h2><div className="hint">กดที่แถวเพื่อดูรายละเอียดและจัดการบัญชี</div></div>
        <button className="btn sm" style={{ marginLeft: 'auto' }} onClick={() => setShowAdd(!showAdd)}>+ เพิ่มพนักงาน</button>
      </div>
      {showAdd && (
        <div style={{ border: '1px solid var(--line2)', padding: 16, marginTop: 12 }}>
          <div className="formgrid">
            <div><label className="mini">ชื่อที่แสดงในระบบ</label><input value={nf.name} onChange={e => setNf({ ...nf, name: e.target.value })} placeholder="เช่น ไอซ์" /></div>
            <div><label className="mini">อีเมล (ใช้เข้าระบบ + รับลิงก์ตั้งรหัส)</label><input type="email" value={nf.email} onChange={e => setNf({ ...nf, email: e.target.value })} placeholder="name@campstudio.co.th" /></div>
            <div><label className="mini">ระดับ</label>
              <select value={nf.role} onChange={e => setNf({ ...nf, role: e.target.value })} style={{ width: '100%' }}>
                {roles.filter(r => r.key !== 'owner').map(r => <option key={r.key} value={r.key}>{r.name}</option>)}
              </select></div>
          </div>
          <div className="hint" style={{ marginTop: 10 }}>ระบบจะส่งลิงก์ตั้งรหัสผ่านไปที่อีเมล ให้พนักงานตั้งรหัสเอง — ไม่มีใครเห็นรหัสของใคร</div>
          <button className="btn" style={{ marginTop: 8 }} disabled={busy} onClick={addStaff}>{busy ? 'กำลังสร้าง…' : 'สร้างบัญชี + ส่งลิงก์ตั้งรหัส'}</button>
        </div>
      )}
      <table style={{ marginTop: 14 }}>
        <thead><tr><th>ชื่อ</th><th>ระดับ</th><th>สาขา</th><th>เข้าระบบล่าสุด</th><th>สถานะ</th><th></th></tr></thead>
        <tbody>
          {staff.map(s => (
            <tr key={s.id} className="clickrow" onClick={() => openStaff(s)}>
              <td><div style={{ fontWeight: 600 }}>{s.name}</div>
                <div className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>{s.emp_code || ''}</div></td>
              <td>{roleName(s.role)}{s.twofa ? <> <span className="chip c-free">2FA</span></> : null}</td>
              <td>{s.branch_name || '—'}</td>
              <td className="mono" style={{ fontSize: 12 }}>{s.last_seen ? thDateTime(s.last_seen) + ' ' + thTime(s.last_seen) : 'ยังไม่เคยเข้า'}</td>
              <td>{s.is_active ? <span className="chip c-out">ใช้งาน</span> : <span className="chip c-fix">ปิดใช้งาน</span>}</td>
              <td className="r"><button className="btn ghost sm" onClick={e => { e.stopPropagation(); sendPwLink(s); }}>ส่งลิงก์ตั้งรหัส</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- คลังต้นทางของอุปกรณ์เช่า ----------
// ของที่ให้เช่าได้ = ของที่อยู่ในคลังที่ติ๊กไว้ตรงนี้ (อ่านจาก StockPoint)
function BranchPicker() {
  const { can } = useAuth();
  const { refresh } = useData();
  const { toast } = useUI();
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!can('system.edit')) return;
    rpc('rp_wh_branches')
      .then(r => { setRows(r); setSel(new Set(r.filter(x => x.selected).map(x => x.id))); })
      .catch(e => setErr(e.message));
  }, [can]);

  if (!can('system.edit')) return null;

  const toggle = (id) => setSel(s => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const save = async () => {
    if (!sel.size) { toast('ติ๊กคลังอย่างน้อย 1 คลัง'); return; }
    setBusy(true);
    try {
      await rpc('rp_set_sync_branches', { p_ids: [...sel] });
      const r = await rpc('rp_sync_stockpoint');
      toast(`บันทึกแล้ว — ดึงของเข้าระบบ: สินค้าใหม่ ${r.new_skus} · ชิ้นใหม่ ${r.new_units}`);
      await refresh();
    } catch (e) { toast(e.message); }
    setBusy(false);
  };

  const dirty = rows && (rows.filter(r => r.selected).length !== sel.size ||
    rows.some(r => r.selected !== sel.has(r.id)));

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <h2>คลังไหนที่เอาของมาให้เช่าได้</h2>
      <div className="hint">
        ติ๊กคลังของ StockPoint ที่จะให้ RentPoint ดึงของมาปล่อยเช่า — ของในคลังที่ไม่ติ๊กจะไม่โผล่ในระบบเช่า
        · เปลี่ยนแล้วกดบันทึก ระบบจะดึงของเข้ามาให้ทันที
      </div>
      {err && <div className="note show">{err}</div>}
      {!rows ? <div className="empty">กำลังโหลดรายชื่อคลัง…</div> : (
        <table style={{ marginTop: 6 }}>
          <thead><tr>
            <th style={{ width: 54 }}>ใช้</th><th>คลัง</th>
            <th className="r">รายการ</th><th className="r">ชิ้น</th><th>ประเภท</th>
          </tr></thead>
          <tbody>
            {rows.filter(r => r.pieces > 0 || sel.has(r.id)).map(r => (
              <tr key={r.id} className="clickrow" onClick={() => toggle(r.id)}>
                <td><input type="checkbox" className="psw" checked={sel.has(r.id)} readOnly /></td>
                <td>
                  <div style={{ fontWeight: 600 }}>{r.name}</div>
                  <div className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>{r.id}</div>
                </td>
                <td className="r mono">{r.skus}</td>
                <td className="r mono">{r.pieces}</td>
                <td>{!r.sellable && !r.countable
                  ? <span className="chip c-due">คลังเดโม่ / ไม่ขาย</span>
                  : <span className="chip c-free">คลังขายปกติ</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn" disabled={busy || !dirty} onClick={save}>
          {busy ? 'กำลังบันทึก…' : 'บันทึก + ดึงของเข้าระบบ'}
        </button>
        {dirty && <span className="hint" style={{ margin: 0 }}>ยังไม่ได้บันทึกการเปลี่ยนแปลง</span>}
      </div>
      <div className="note show" style={{ marginTop: 12 }}>
        ระบบจะ<b>เพิ่ม</b>ของให้ครบตามจำนวนในคลังเท่านั้น — ไม่ลบชิ้นที่มีอยู่แล้วอัตโนมัติ (อาจติดสัญญาหรือมีประวัติเช่า)
        · สินค้าที่ไม่มีของเหลือในคลังจะถูกปิดไม่ให้เลือกเช่าใหม่ แต่ชิ้นเดิมยังอยู่ในคลังเช่า
      </div>
    </div>
  );
}

// ---------- ค่าระบบ ----------
function SysTab() {
  const { can, settings, vatRate, whtRate, reloadBoot } = useAuth();
  const { toast } = useUI();
  const [v, setV] = useState({
    break_even_trips: Number(settings.break_even_trips ?? 10),
    credit_days: Number(settings.credit_days ?? 15),
    deposit_cap: Number(settings.deposit_cap ?? 10000),
    late_fee_pct: Math.round(Number(settings.late_fee_pct ?? 0.35) * 100),
    id_purge_days: Number(settings.id_purge_days ?? 90),
    session_timeout_min: Number(settings.session_timeout_min ?? 30),
  });
  const [busy, setBusy] = useState(false);
  const editable = can('system.edit');

  const save = async () => {
    setBusy(true);
    try {
      await rpc('rp_set_setting', { p_key: 'break_even_trips', p_value: v.break_even_trips });
      await rpc('rp_set_setting', { p_key: 'credit_days', p_value: v.credit_days });
      await rpc('rp_set_setting', { p_key: 'deposit_cap', p_value: v.deposit_cap });
      await rpc('rp_set_setting', { p_key: 'late_fee_pct', p_value: v.late_fee_pct / 100 });
      await rpc('rp_set_setting', { p_key: 'id_purge_days', p_value: v.id_purge_days });
      await rpc('rp_set_setting', { p_key: 'session_timeout_min', p_value: v.session_timeout_min });
      toast('บันทึกค่าระบบแล้ว · ลงประวัติการใช้งานเรียบร้อย');
      await reloadBoot();
    } catch (e) { toast(e.message); }
    setBusy(false);
  };

  const num = (k) => ({
    value: v[k], disabled: !editable,
    onChange: (e) => setV(x => ({ ...x, [k]: +e.target.value })),
  });

  return (<>
    <BranchPicker />
    <div className="grid g2 setpane" style={{ marginTop: 12 }}>
      <div className="card">
        <h2>ภาษีและอัตรา</h2>
        <div className="formgrid">
          <div><label className="mini">VAT ปัจจุบัน (จากตาราง tax_rates)</label>
            <input className="mono-in" value={Math.round(vatRate * 100) + '%'} disabled /></div>
          <div><label className="mini">หัก ณ ที่จ่าย นิติบุคคล</label>
            <input className="mono-in" value={Math.round(whtRate * 100) + '%'} disabled /></div>
          <div><label className="mini">รอบคืนทุนต่อชิ้น</label><input className="mono-in" type="number" {...num('break_even_trips')} /></div>
          <div><label className="mini">อายุเครดิต (วัน)</label><input className="mono-in" type="number" {...num('credit_days')} /></div>
          <div><label className="mini">เพดานมัดจำต่อชิ้น</label><input className="mono-in" type="number" {...num('deposit_cap')} /></div>
          <div><label className="mini">ค่าปรับคืนช้า (%/คืน)</label><input className="mono-in" type="number" {...num('late_fee_pct')} /></div>
        </div>
        <div className="note show" style={{ marginTop: 14 }}>
          อัตรา VAT ตามกฎหมายคือ 10% ที่ลดเหลือ 7% ด้วยพระราชกฤษฎีกาที่ต่ออายุเป็นรอบ —
          ระบบเก็บเป็นตาราง <b>tax_rates</b> ที่มีวันเริ่ม-สิ้นสุด (รอบล่าสุดถึง 30 ก.ย. 2570)
          การแก้อัตราภาษีให้เพิ่มแถวใหม่ในตาราง ไม่แก้ค่าเดิม
        </div>
        {editable && <button className="btn" style={{ marginTop: 14 }} disabled={busy} onClick={save}>บันทึกค่าระบบ</button>}
        {!editable && <div className="hint" style={{ marginTop: 12 }}>แก้ไขได้เฉพาะผู้มีสิทธิ์ "แก้ไขค่าระบบ ภาษี และอัตรา"</div>}
      </div>
      <div className="card">
        <h2>ความปลอดภัยและข้อมูลส่วนบุคคล</h2>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13.5 }}>
          <label className="chk"><input type="checkbox" checked readOnly /> บันทึกประวัติทุกครั้งที่เปิดดูเอกสารยืนยันตัวตน (บังคับ ปิดไม่ได้)</label>
          <label className="chk"><input type="checkbox" checked readOnly /> เอกสารยืนยันตัวตนอยู่ใน bucket ส่วนตัว เข้าถึงผ่านลิงก์ชั่วคราว ≤ 5 นาที</label>
          <label className="chk"><input type="checkbox" checked readOnly /> ลบเอกสารยืนยันตัวตนอัตโนมัติหลังปิดสัญญา</label>
        </div>
        <div className="formgrid" style={{ marginTop: 6 }}>
          <div><label className="mini">เก็บเอกสารยืนยันตัวตน (วัน)</label><input className="mono-in" type="number" {...num('id_purge_days')} /></div>
          <div><label className="mini">ออกจากระบบอัตโนมัติ (นาที)</label><input className="mono-in" type="number" {...num('session_timeout_min')} /></div>
        </div>
      </div>
    </div>
  </>);
}
